<template>
    <div class="classic-import">
        <el-alert type="warning" show-icon :closable="false" title="Classic 数据导入（后门）"
                  description="前端 fetch Classic 源数据，每条记录经过中间件代码加工后直接 POST 给 jigsaw-api 落库。后端只负责按 id 保存，不做映射。"/>

        <el-card shadow="never" style="margin-top: 16px;">
            <template #header>连接配置（Classic 源）</template>
            <el-form label-width="140px" inline>
                <el-form-item label="查询 API 地址">
                    <el-input v-model="apiUrl" placeholder="http://host:port/open/v1/image" style="width: 520px"
                              @change="saveConfig"/>
                </el-form-item>
                <el-form-item label="Import Token">
                    <el-input v-model="importToken" placeholder="可选，X-Import-Token" style="width: 220px"
                              show-password @change="saveConfig"/>
                </el-form-item>
                <el-form-item label="返回上限">
                    <el-input v-model="apiLimit" placeholder="留空=不限制" style="width: 140px"
                              @change="saveConfig"/>
                </el-form-item>
            </el-form>
        </el-card>

        <el-card shadow="never" style="margin-top: 16px;">
            <template #header>
                <div style="display: flex; align-items: center; gap: 12px;">
                    <span>中间件代码（function body，参数 record；禁止 fetch/XMLHttpRequest 等网络 API）</span>
                    <el-button size="small" @click="resetMiddleware">恢复默认模板</el-button>
                    <el-button size="small" :disabled="!selectedRows.length" @click="dryRun">试运行第一条</el-button>
                </div>
            </template>
            <el-input v-model="middlewareCode" type="textarea" :autosize="{minRows: 8, maxRows: 24}"
                      spellcheck="false" class="code-editor" @change="saveConfig"/>
            <div v-if="dryRunResult !== null" style="margin-top: 8px;">
                <div style="font-size: 12px; color: #909399;">试运行结果（仅预览，不保存）：</div>
                <pre class="dry-run">{{ dryRunResult }}</pre>
            </div>
        </el-card>

        <el-card shadow="never" style="margin-top: 16px;">
            <template #header>查询条件</template>
            <el-form :inline="true" label-width="90px">
                <el-form-item label="ID">
                    <el-input v-model="query.id" clearable style="width: 200px"/>
                </el-form-item>
                <el-form-item label="分类">
                    <el-select v-model="query.category" clearable filterable style="width: 220px"
                               :loading="categoriesLoading" placeholder="选择 Classic 分类">
                        <el-option v-for="c in categories" :key="c.key" :value="c.key"
                                   :label="`${c.i18n_name?.EN || c.i18n_name?.en || c.key} (${c.key})`"/>
                    </el-select>
                </el-form-item>
                <el-form-item label="来源">
                    <el-input v-model="query.source" clearable style="width: 120px"/>
                </el-form-item>
                <el-form-item label="模块">
                    <el-select v-model="query.module" clearable style="width: 120px">
                        <el-option label="图库(1)" :value="1"/>
                        <el-option label="Daily(2)" :value="2"/>
                        <el-option label="Story(3)" :value="3"/>
                        <el-option label="集合(4)" :value="4"/>
                    </el-select>
                </el-form-item>
                <el-form-item label="激励视频">
                    <el-select v-model="query.video" clearable style="width: 90px">
                        <el-option label="是" :value="true"/>
                        <el-option label="否" :value="false"/>
                    </el-select>
                </el-form-item>
                <el-form-item label="已上线">
                    <el-select v-model="query.online" clearable style="width: 90px">
                        <el-option label="是" :value="true"/>
                        <el-option label="否" :value="false"/>
                    </el-select>
                </el-form-item>
                <el-form-item label="上线起">
                    <el-input v-model="query.start" placeholder="YYYY-MM-DD" style="width: 130px"/>
                </el-form-item>
                <el-form-item label="上线止">
                    <el-input v-model="query.end" placeholder="YYYY-MM-DD" style="width: 130px"/>
                </el-form-item>
                <el-form-item label="产品筛选">
                    <el-input v-model="query.product" placeholder="classic 等" style="width: 120px"/>
                </el-form-item>
                <el-form-item>
                    <el-button type="primary" :loading="loading" @click="search">查询</el-button>
                </el-form-item>
            </el-form>
        </el-card>

        <div style="margin: 12px 0; display: flex; gap: 12px; align-items: center;">
            <span>已选 {{ selectedRows.length }} / {{ allRows.length }} 条</span>
            <el-button type="success" :loading="importing" :disabled="!selectedRows.length"
                       @click="batchImport">批量导入</el-button>
            <el-button :disabled="!allRows.length" @click="selectAll">全选</el-button>
            <el-button :disabled="!selectedRows.length" @click="clearAll">全不选</el-button>
        </div>

        <el-table ref="tableRef" :data="pagedRows" v-loading="loading"
                  @selection-change="onSelectionChange" row-key="id" border>
            <el-table-column type="selection" width="48" reserve-selection/>
            <el-table-column label="ID" prop="id" width="210"/>
            <el-table-column label="缩略图" width="100">
                <template #default="scope">
                    <el-image v-if="scope.row.thumbnail_url || scope.row.thumbnail"
                              :src="scope.row.thumbnail_url || thumbUrl(scope.row.thumbnail)" style="width: 80px"/>
                </template>
            </el-table-column>
            <el-table-column label="模块" prop="module" width="80"/>
            <el-table-column label="来源" prop="source" width="100"/>
            <el-table-column label="上线" prop="online_date" width="110"/>
            <el-table-column label="视频" width="70">
                <template #default="scope">{{ scope.row.video ? '是' : '否' }}</template>
            </el-table-column>
            <el-table-column label="产品" prop="product" width="120"/>
            <el-table-column label="状态" prop="status" width="90"/>
        </el-table>

        <el-pagination style="margin-top: 12px" layout="total, sizes, prev, pager, next"
                       :total="allRows.length" :page-sizes="[10, 20, 50, 100]"
                       v-model:page-size="pageSize" v-model:current-page="page"/>

        <el-dialog v-model="resultVisible" title="导入结果" width="600px">
            <p>新建 {{ importResult.created?.length || 0 }}，更新 {{ importResult.updated?.length || 0 }}，
                跳过 {{ importResult.skipped?.length || 0 }}，失败 {{ importResult.errors?.length || 0 }}</p>
            <el-scrollbar max-height="300px" v-if="importResult.errors?.length">
                <pre style="white-space: pre-wrap; font-size: 12px;">{{ JSON.stringify(importResult.errors, null, 2) }}</pre>
            </el-scrollbar>
        </el-dialog>
    </div>
</template>

<script>
import axios from 'ts-axios-new';
import {ElMessage, ElMessageBox} from 'element-plus';
import {getLocalObj, setLocalObj} from '../libs/storage';

const STORAGE_KEY = 'classic_import_config';
const LEGACY_GATE_KEY = 'jigsaw_gate_classic_import';

export const CLASSIC_IMPORT_GATE = 'classic-import';

const DEFAULT_MIDDLEWARE = `// 参数 record：来自 Classic 的单条原始记录
// 返回最终要写入 jigsaw 的对象；返回 null/undefined 则跳过这条
const out = { ...record };

// 去掉源端附加字段，避免污染目标库
delete out.thumbnail_url;
delete out.resource_url;
delete out.current_product;

// 例：把当前条目挂到 peace 产品上，模块写到 modules 字典里
out.product = ['peace'];
out.modules = { peace: 'LIBRARY' };

// 例：把 Classic 的 video 翻成 cost
if (out.video) out.cost = 20;

return out;
`;

/** 中间件内禁止直接访问的全局 API（仅拦网络/跨窗，其它内置对象照常使用） */
const MIDDLEWARE_BLOCKED = new Set([
    'fetch', 'XMLHttpRequest', 'WebSocket', 'EventSource',
    'Request', 'Response', 'Headers', 'sendBeacon',
    'importScripts',
    'window', 'globalThis', 'self', 'parent', 'top', 'opener',
    'navigator', 'location',
]);

function createBlockSandbox() {
    return new Proxy(Object.create(null), {
        has(_, key) {
            return MIDDLEWARE_BLOCKED.has(key);
        },
        get(_, key) {
            throw new ReferenceError(`'${String(key)}' is blocked in middleware`);
        },
    });
}

let gateUnlocked = false;

try {
    sessionStorage.removeItem(LEGACY_GATE_KEY);
} catch (e) { /* ignore */ }

export function isClassicImportGateEnabled(route) {
    if (route?.query?.gate === CLASSIC_IMPORT_GATE) {
        gateUnlocked = true;
        return true;
    }
    return gateUnlocked;
}

export default {
    name: 'ClassicImport',
    data() {
        const cfg = getLocalObj(STORAGE_KEY, {});
        return {
            apiUrl: cfg.apiUrl || 'http://localhost:20012/cms/v1/open/image',
            importToken: cfg.importToken || '',
            apiLimit: cfg.apiLimit || '',
            classicProduct: 'classic',
            middlewareCode: cfg.middlewareCode || DEFAULT_MIDDLEWARE,
            query: {
                id: null,
                category: null,
                source: null,
                module: null,
                video: null,
                online: null,
                start: null,
                end: null,
                product: null,
                include_none_date: true,
            },
            allRows: [],
            page: 1,
            pageSize: 20,
            loading: false,
            importing: false,
            selectedRows: [],
            resultVisible: false,
            importResult: {},
            categories: [],
            categoriesLoading: false,
            dryRunResult: null,
        };
    },
    computed: {
        pagedRows() {
            const start = (this.page - 1) * this.pageSize;
            return this.allRows.slice(start, start + this.pageSize);
        },
    },
    watch: {
        pageSize() {
            this.page = 1;
        },
        apiUrl() {
            this.loadCategories();
        },
        importToken() {
            this.loadCategories();
        },
    },
    methods: {
        saveConfig() {
            setLocalObj(STORAGE_KEY, {
                apiUrl: this.apiUrl,
                importToken: this.importToken,
                apiLimit: this.apiLimit,
                middlewareCode: this.middlewareCode,
            });
        },
        resetMiddleware() {
            this.middlewareCode = DEFAULT_MIDDLEWARE;
            this.dryRunResult = null;
            this.saveConfig();
        },
        compileMiddleware() {
            try {
                const wrapped = `with (sandbox) {\n${this.middlewareCode}\n}`;
                return new Function('record', 'sandbox', wrapped);
            } catch (e) {
                ElMessage.error(`中间件代码解析失败：${e.message}`);
                return null;
            }
        },
        runMiddleware(record) {
            const fn = this.compileMiddleware();
            if (!fn) return {ok: false, error: 'compile failed'};
            try {
                const result = fn(JSON.parse(JSON.stringify(record)), createBlockSandbox());
                return {ok: true, result};
            } catch (e) {
                return {ok: false, error: e.message};
            }
        },
        dryRun() {
            if (!this.selectedRows.length) return;
            const {ok, result, error} = this.runMiddleware(this.selectedRows[0]);
            if (!ok) {
                this.dryRunResult = `执行抛错：${error}`;
                return;
            }
            this.dryRunResult = result === null || result === undefined
                ? '(返回 null/undefined，将被跳过)'
                : JSON.stringify(result, null, 2);
        },
        thumbUrl(path) {
            if (!path) return '';
            if (path.startsWith('http')) return path;
            return `${this.$root.cdn_url}/${path}`;
        },
        buildClassicUrl(path, params) {
            const base = new URL(this.apiUrl);
            const url = new URL(path, `${base.protocol}//${base.host}`);
            Object.entries(params || {}).forEach(([k, v]) => {
                if (v !== null && v !== undefined && v !== '') {
                    url.searchParams.set(k, String(v));
                }
            });
            return url.toString();
        },
        classicHeaders() {
            const headers = {product: this.classicProduct};
            if (this.importToken) {
                headers['X-Import-Token'] = this.importToken;
            }
            return headers;
        },
        async classicFetch(path, params) {
            const res = await fetch(this.buildClassicUrl(path, params), {headers: this.classicHeaders()});
            const json = await res.json();
            if (!res.ok) {
                throw new Error(json?.status?.message || json?.message || res.statusText);
            }
            return json?.data ?? json;
        },
        async loadCategories() {
            if (!this.apiUrl) return;
            try {
                new URL(this.apiUrl);
            } catch (e) {
                return;
            }
            this.categoriesLoading = true;
            try {
                const data = await this.classicFetch('/cms/v1/open/category', {current_product: this.classicProduct});
                this.categories = data.categoryList || [];
            } catch (e) {
                ElMessage.error(`分类加载失败：${e.message || ''}`);
            } finally {
                this.categoriesLoading = false;
            }
        },
        async search() {
            if (!this.apiUrl) {
                ElMessage.warning('请填写查询 API 地址');
                return;
            }
            try {
                new URL(this.apiUrl);
            } catch (e) {
                ElMessage.error('查询 API 地址格式不正确，请填写完整 URL（含 http(s)://）');
                return;
            }
            const params = {...this.query, current_product: this.classicProduct};
            const limit = parseInt(this.apiLimit, 10);
            if (Number.isFinite(limit) && limit > 0) {
                params.limit = limit;
            }
            const base = new URL(this.apiUrl);
            this.loading = true;
            try {
                const data = await this.classicFetch(base.pathname, params);
                this.allRows = data.imageList || [];
                this.page = 1;
                this.clearAll();
                if (data.truncated) {
                    ElMessage.warning(`结果超过上限，已截断到 ${this.allRows.length} 条，请收紧筛选条件`);
                }
            } catch (e) {
                ElMessage.error(e.message || '查询失败');
            } finally {
                this.loading = false;
            }
        },
        onSelectionChange(rows) {
            this.selectedRows = rows;
        },
        selectAll() {
            const ref = this.$refs.tableRef;
            if (!ref) return;
            this.allRows.forEach(row => ref.toggleRowSelection(row, true));
        },
        clearAll() {
            const ref = this.$refs.tableRef;
            if (ref) ref.clearSelection();
            this.selectedRows = [];
        },
        async batchImport() {
            if (!this.selectedRows.length) return;
            const fn = this.compileMiddleware();
            if (!fn) return;
            const sandbox = createBlockSandbox();

            const items = [];
            const transformErrors = [];
            for (const row of this.selectedRows) {
                try {
                    const result = fn(JSON.parse(JSON.stringify(row)), sandbox);
                    if (result === null || result === undefined) {
                        continue;
                    }
                    if (typeof result !== 'object') {
                        transformErrors.push({id: row.id, message: `middleware returned non-object: ${typeof result}`});
                        continue;
                    }
                    items.push(result);
                } catch (e) {
                    transformErrors.push({id: row.id, message: e.message});
                }
            }

            if (!items.length) {
                ElMessage.warning(`没有可导入的记录（被跳过/出错 ${transformErrors.length} 条）`);
                if (transformErrors.length) {
                    this.importResult = {errors: transformErrors};
                    this.resultVisible = true;
                }
                return;
            }

            await ElMessageBox.confirm(
                `将原样保存 ${items.length} 条到 jigsaw-api（已选 ${this.selectedRows.length}，跳过/出错 ${this.selectedRows.length - items.length}）`,
                '确认导入',
                {type: 'warning'},
            );
            this.importing = true;
            try {
                const res = await axios.post('/cms/v1/import/classic', {items});
                this.importResult = res.data.data || {};
                if (transformErrors.length) {
                    this.importResult.errors = [...(this.importResult.errors || []), ...transformErrors];
                }
                this.resultVisible = true;
                ElMessage.success('导入完成');
            } catch (e) {
                ElMessage.error(e.response?.data?.status?.message || e.message || '导入失败');
            } finally {
                this.importing = false;
            }
        },
    },
    mounted() {
        this.saveConfig();
        this.loadCategories();
    },
};
</script>

<style scoped>
.classic-import {
    padding: 8px;
}
.code-editor :deep(textarea) {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
    font-size: 12px;
    line-height: 1.5;
    tab-size: 2;
}
.dry-run {
    background: #f5f7fa;
    border: 1px solid #ebeef5;
    border-radius: 4px;
    padding: 8px;
    margin: 0;
    max-height: 240px;
    overflow: auto;
    white-space: pre-wrap;
    font-size: 12px;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}
</style>
